import { getUpdateSchema } from "@source/pages/HomePage/homeRouter/updateTab/updateSchema/updateFormSchema";
import {
  Sections,
  IUpdateStep,
  UpdateInputs,
  UpdateInputsTypes,
  SubSections,
  SectionsId,
  SubSectionsId,
  Infection_SubSectionsId,
  GP_Review_Medication_Change_SubSectionsId,
  Fall_SubSections,
  Fall_SubSectionsId,
  Infection_SubSections,
  GP_Review_Medication_Change_SubSections,
  UpdatePriorities,
  UpdatePrioritiesId,
  Incident_SubSectionsId,
  Incident_SubSections,
  Change_To_Condition_SubSections,
  Change_To_Condition_SubSectionsId,
  Medication_Incidents_SubSectionsId,
  Medication_Incidents_SubSections,
  Pain_SubSections,
  Pain_SubSectionsId,
  UpdateInput,
  UpdateMessageSymbols,
  Request_For_Items_SubSections,
  Personal_Care_SubSections,
  Nutritional_SubSections,
  Specialist_Appointment_SubSections,
  Other_SubSections,
  Request_For_Items_SubSectionsId,
  Personal_Care_SubSectionsId,
  Nutritional_SubSectionsId,
  Specialist_Appointment_SubSectionsId,
  Other_SubSectionsId,
  Resident_Of_The_Day_SubSectionsId,
  Return_From_Hospital_SubSections,
  Return_From_Hospital_SubSectionsId,
  Resident_Of_The_Day_SubSections,
  Wound_Care_SubSections,
  Wound_Care_SubSectionsId,
  IUpdateCheckboxInputItem,
  Physiotherapist_SubSections,
  Physiotherapist_SubSectionsId,
  Vaccination_SubSectionsId,
  Vaccination_SubSections,
  Social_SubSections, Social_SubSectionsId,
  Monthly_Care_Statement_SubSections,
  MonthlyCareStatement_SubSectionsId,
  IUpdateValues, Nurse_Practitioner_SubSections, Nurse_Practitioner_SubSectionsId,
} from "@source/pages/HomePage/homeRouter/updateTab/updateSchema/updateFormModels";
import { IMainUpdateForm } from "@source/pages/HomePage/homeRouter/updateTab/updateTab";
import styles from "./updateForm.module.scss";
import {deepClone} from "@source/utils/utils";

const UpdateSchema = getUpdateSchema()

export function formattedFloat(number: number, precision: number): string {
  return parseFloat(number.toFixed(precision)).toString();
}

export function getWeightGainOrLoss(current: number, previous: number): string {
  return current > previous ? 'gain' : 'loss'
}

export function getWeightPercentageChange(current: number, previous: number): string {
  const percentageChange = Math.abs((current * 100 / previous) - 100);
  return formattedFloat(percentageChange, 1)
}

export function getSections() {
  return Object.keys(UpdateSchema)
    .filter((section) => section !== Sections.Monthly_Care_Statement)
    .map((section) => section)
}

export function getSubSections(section: Sections):Array<SubSections> {
  return Object.keys(UpdateSchema[section]).map((subSection) => subSection) as Array<SubSections>
}

export function generateStepInitialValues(inputs: UpdateInputs): IUpdateValues {
  const addInputsValues = (inputs: UpdateInputs) => {
    let values: IUpdateValues = {};

    inputs.forEach((input) => {
      switch(input.type) {
        case UpdateInputsTypes.Checkbox: {
          values[input.name] = input.checked;
          break;
        }
        case UpdateInputsTypes.CheckboxInput: {
          values[input.name] = input.value;
  
          if(input.dependentInputs) {
            Object.values(input.dependentInputs).forEach((inputs) => {
              values = {...values, ...addInputsValues(inputs)}
            })
          }
          break;
        }
        case UpdateInputsTypes.RadioInput: {
          values[input.name] = input.value;
  
          if(input.dependentInputs) {
            Object.values(input.dependentInputs).forEach((inputs) => {
              values = {...values, ...addInputsValues(inputs)}
            })
          }
          break;
        }
        case UpdateInputsTypes.File: {
          values['files'] = input.value || [];
          break;
        }
        default: {
          values[input.name] = input.value;
          break;
        }
      }
    })

    return values
  }
  return addInputsValues(inputs)
}

export function generateValidationSchema(inputs: UpdateInputs, values: IUpdateValues) {
  let validateSchema = {};

  const addValidationSchema = (input: UpdateInput) => {
    const {name, validationSchema} = input;
    validateSchema[name] = validationSchema;
  }

  const addDependentInputs = (inputs: UpdateInputs) => {
    inputs.forEach((dependentInput) => {
      addValidationSchema(dependentInput)
    });

    validateSchema = {...validateSchema, ...generateValidationSchema(inputs, values)};
  }

  for (let i=0; i<inputs.length; i++) {
    const input = inputs[i];

    if(input.validationSchema) {
      if(checkInputCondition(inputs, input, values, {} as never as IMainUpdateForm)) {
        addValidationSchema(input);
      }
    }

    if('dependentInputs' in input && input?.dependentInputs && values[input.name]) {
      for(let nameDependentValue in input.dependentInputs) {
        if(Array.isArray(values[input.name])) {
          if(values[input.name].includes(nameDependentValue)) {
            addDependentInputs(input.dependentInputs[nameDependentValue]);
          }
        } else {
          if(values[input.name] === nameDependentValue) {
            addDependentInputs(input.dependentInputs[nameDependentValue]);
          }
        }

      }
    }
    
  }

  return validateSchema;
}

export function checkInputCondition(inputs: UpdateInputs, input: UpdateInput, values: IUpdateValues, valuesMain: IMainUpdateForm) {
  if(input.conditions) {
    for (let i=0; i<input.conditions.length; i++) {
      const conditionName = Object.keys(input.conditions[i])[0];
      const conditionInputType = inputs.find((input) => input.name === conditionName)?.type;
      if(conditionInputType === UpdateInputsTypes.CheckboxInput) {
        if(!values[conditionName]?.includes(input.conditions[i][conditionName])) {
          return false;
        }
      } else {
        if(!(values[conditionName] === input.conditions[i][conditionName] || valuesMain[conditionName] === input.conditions[i][conditionName])) {
          return false;
        }
      }
    }
  }
  return true;
}

export function checkNestedInputConditions(inputs: UpdateInputs, checkboxItem: IUpdateCheckboxInputItem, values: IUpdateValues, valuesMain: IMainUpdateForm) {
  if(checkboxItem.conditions) {
    for (let i=0; i<checkboxItem.conditions.length; i++) {
      const conditionName = Object.keys(checkboxItem.conditions[i])[0];
      const conditionInputType = inputs.find((input) => input.name === conditionName)?.type;
      if(values[conditionName] === checkboxItem.conditions[i][conditionName] || valuesMain[conditionName] === checkboxItem.conditions[i][conditionName]) {
        return true;
      }
    }
    return false;
  }
  return true;
}

export function getSectionNameById(sectionId: SectionsId): Sections {
  const Categories = {
    [SectionsId.Fall]: Sections.Fall,
    [SectionsId.Infection]: Sections.Infection,
    [SectionsId.Vaccination]: Sections.Vaccination,
    [SectionsId.GP_Review_Medication_Change]: Sections.GP_Review_Medication_Change,
    [SectionsId.Nurse_Practitioner]: Sections.Nurse_Practitioner,
    [SectionsId.Incident]: Sections.Incident,
    [SectionsId.Change_To_Condition]: Sections.Change_To_Condition,
    [SectionsId.Wound_Care]: Sections.Wound_Care,
    [SectionsId.Medication_Incidents]: Sections.Medication_Incidents,
    [SectionsId.Pain]: Sections.Pain,
    [SectionsId.Physiotherapist]: Sections.Physiotherapist,
    [SectionsId.Request_For_Items]: Sections.Request_For_Items,
    [SectionsId.Personal_Care]: Sections.Personal_Care,
    [SectionsId.Nutritional]: Sections.Nutritional,
    [SectionsId.Specialist_Appointment]: Sections.Specialist_Appointment,
    [SectionsId.Other]: Sections.Other,
    [SectionsId.Resident_Of_The_Day]: Sections.Resident_Of_The_Day,
    [SectionsId.Return_From_Hospital]: Sections.Return_From_Hospital,
    [SectionsId.Social]: Sections.Social,
    [SectionsId.Monthly_Care_Statement]: Sections.Monthly_Care_Statement,
  }

  return Categories[sectionId]
}

export function getViewUpdateCategoryName(section: Sections | '', subSection: SubSections | '', viewName?: string) {
  const sections = {
    [Sections.Fall]: {
      [Fall_SubSections.General]: {category: 'Fall', subcategory: '', updateCategoryName: 'Fall'},
      [Fall_SubSections.Post_Fall_Review]: {category: 'Fall', subcategory: 'Fall - Post Fall Review', updateCategoryName: 'Fall - Post Fall Review'},
    },
    [Sections.Infection]: {
      [Infection_SubSections.Urinary_Infection]: {category: 'Infection', subcategory: 'Urinary', updateCategoryName: 'Urinary Infection'},
      [Infection_SubSections.Respiratory]: {category: 'Infection', subcategory: 'Respiratory', updateCategoryName: 'Respiratory Infection'},
      [Infection_SubSections.Wound]: {category: 'Infection', subcategory: 'Wound', updateCategoryName: 'Wound Infection'},
      [Infection_SubSections.Skin_Infection]: {category: 'Infection', subcategory: 'Skin infection', updateCategoryName: 'Skin Infection'},
      [Infection_SubSections.Cellulitis]: {category: 'Infection', subcategory: 'Cellulitis', updateCategoryName: 'Cellulitis Infection'},
      [Infection_SubSections.Ear_Nose_Throat_ENT]: {category: 'Infection', subcategory: 'Ear, Nose, Throat ENT', updateCategoryName: 'Ear, Nose and Throat Infection'},
      [Infection_SubSections.Eye]: {category: 'Infection', subcategory: 'Eye', updateCategoryName: 'Eye Infection'},
      [Infection_SubSections.Covid_First_Update_To_Family]: {category: 'Infection', subcategory: 'Covid-19 Infection -  First Update', updateCategoryName: 'Covid-19 Infection -  First Update'},
      [Infection_SubSections.Covid_Ongoing_Update]: {category: 'Infection', subcategory: 'Covid-19 Infection - Ongoing Update', updateCategoryName: 'Covid-19 Infection - Ongoing Update'},
      [Infection_SubSections.Other]: {category: 'Infection', subcategory: 'Unknown infection', updateCategoryName: 'Unknown Infection'},
    },
    [Sections.Vaccination]: {
      [Vaccination_SubSections.Vaccination]: {category: 'Vaccination Administration', subcategory: '', updateCategoryName: 'Vaccination Administration'}
    },
    [Sections.GP_Review_Medication_Change]: {
      [GP_Review_Medication_Change_SubSections.General_Review]: {category: 'GP Review', subcategory: 'General', updateCategoryName: 'GP Review - General'},
      [GP_Review_Medication_Change_SubSections.Deterioration_Physical_Or_Cognitive]: {category: 'GP Review', subcategory: 'Condition', updateCategoryName: 'GP Review - Condition'},
      [GP_Review_Medication_Change_SubSections.Medication_Review]: {category: 'GP Review', subcategory: 'Medication', updateCategoryName: 'GP Review - Medication'} ,
      [GP_Review_Medication_Change_SubSections.Review_Post_Fall_Or_Incident]: {category: 'GP Review', subcategory: 'After Incident', updateCategoryName: 'GP Review - After Incident'} ,
      [GP_Review_Medication_Change_SubSections.Other]: {category: 'GP Review', subcategory: 'Other', updateCategoryName: 'GP Review - Other'},
    },
    [Sections.Nurse_Practitioner]: {
      [Nurse_Practitioner_SubSections.General_Review]: {category: 'Nurse Practitioner Review', subcategory: 'General', updateCategoryName: 'Nurse Practitioner Review - General'},
      [Nurse_Practitioner_SubSections.Deterioration_Physical_Or_Cognitive]: {category: 'Nurse Practitioner Review', subcategory: 'Condition', updateCategoryName: 'Nurse Practitioner Review - Condition'},
      [Nurse_Practitioner_SubSections.Medication_Review]: {category: 'Nurse Practitioner Review', subcategory: 'Medication', updateCategoryName: 'Nurse Practitioner Review - Medication'} ,
      [Nurse_Practitioner_SubSections.Review_Post_Fall_Or_Incident]: {category: 'Nurse Practitioner Review', subcategory: 'After Incident', updateCategoryName: 'Nurse Practitioner Review - After Incident'} ,
      [Nurse_Practitioner_SubSections.Other]: {category: 'Nurse Practitioner Review', subcategory: 'Other', updateCategoryName: 'Nurse Practitioner Review - Other'},
    },
    [Sections.Incident]: {
      [Incident_SubSections.Bruising]: {category: 'Incident', subcategory: 'Bruising', updateCategoryName: 'Incident - Bruising'},
      [Incident_SubSections.Skin_Tear]: {category: 'Incident', subcategory: 'Skin Tear', updateCategoryName: 'Incident - Skin Tear'},
      [Incident_SubSections.Pressure_Injury]: {category: 'Incident', subcategory: 'Pressure Injury', updateCategoryName: 'Incident - Pressure Injury'},
      [Incident_SubSections.Ulcer]: {category: 'Incident', subcategory: 'Ulcer', updateCategoryName: 'Incident - Ulcer'},
      [Incident_SubSections.Absconding]: {category: 'Incident', subcategory: 'Absconding', updateCategoryName: 'Incident - Absconding'},
      [Incident_SubSections.Behaviour]: {category: 'Incident', subcategory: 'Behaviour', updateCategoryName: 'Behaviour'},
      [Incident_SubSections.Choking]: {category: 'Incident', subcategory: 'Choking', updateCategoryName: 'Incident - Choking'} ,
      [Incident_SubSections.Follow_up]: {category: 'Incident', subcategory: 'Follow-up', updateCategoryName: 'Incident - Follow-up'} ,
      [Incident_SubSections.Incontinence_Associated_Dermatitis]: {category: 'Incident', subcategory: 'Incontinence Associated Dermatitis', updateCategoryName: 'Incident - Incontinence Associated Dermatitis'} ,
      [Incident_SubSections.Other]: {category: 'Incident', subcategory: 'Other incident', updateCategoryName: 'Incident - Other'},
    },
    [Sections.Change_To_Condition]: {
      [Change_To_Condition_SubSections.Change_To_Cognition]: {category: 'Condition Update', subcategory: 'Cognition', updateCategoryName: 'Condition Update - Cognition'},
      [Change_To_Condition_SubSections.Change_To_Mobility]: {category: 'Condition Update', subcategory: 'Mobility', updateCategoryName: 'Condition Update - Mobility'},
      [Change_To_Condition_SubSections.Change_To_Appetite]: {category: 'Condition Update', subcategory: 'Appetite / Nutrition', updateCategoryName: 'Condition Update - Appetite / Nutrition'},
      [Change_To_Condition_SubSections.Change_To_Behaviour_Mood]: {category: 'Condition Update', subcategory: 'Behaviour / Mood', updateCategoryName: 'Condition Update - Behaviour / Mood'},
      [Change_To_Condition_SubSections.Weight_Change_Update]: {category: 'Condition Update', subcategory: 'Weight', updateCategoryName: 'Condition Update - Weight'},
      [Change_To_Condition_SubSections.Other_General_Update]: {category: 'Condition Update', subcategory: 'General', updateCategoryName: 'Condition Update - General'},
    },
    [Sections.Wound_Care]: {
      [Wound_Care_SubSections.Wound_Healed]: {category: 'Wound Care', subcategory: 'Wound Healing', updateCategoryName: 'Wound Care - Wound Healing'},
      [Wound_Care_SubSections.Wound_Review]: {category: 'Wound Care', subcategory: 'Wound Review', updateCategoryName: 'Wound Care - Wound Review'},
    },
    [Sections.Medication_Incidents]: {
      [Medication_Incidents_SubSections.Medication_Incidents]: {category: 'Medication incidents', subcategory: '', updateCategoryName: 'Medication incident'}
    },
    [Sections.Pain]: {
      [Pain_SubSections.Pain]: {category: 'Pain', subcategory: '', updateCategoryName: 'Pain'}
    },
    [Sections.Physiotherapist]: {
      [Physiotherapist_SubSections.Group_Physio_Led_Exercise_Session]: {category: 'Physiotherapist', subcategory: 'Group Physio Led Exercise Session', updateCategoryName: 'Group Physio-Led Exercise Session'},
      [Physiotherapist_SubSections.Initial_Physio_Review]: {category: 'Physiotherapist', subcategory: 'Initial Physio Review', updateCategoryName: 'Initial Physiotherapist Review'},
      [Physiotherapist_SubSections.Physio_Mobility_Equipment_Review]: {category: 'Physiotherapist', subcategory: 'Physio Mobility/Equipment Review', updateCategoryName: 'Physiotherapist Mobility/Equipment review'},
      [Physiotherapist_SubSections.Physio_Post_Fall_Review]: {category: 'Physiotherapist', subcategory: 'Physio Post Fall Review', updateCategoryName: 'Physiotherapist Post Fall Review'},
      [Physiotherapist_SubSections.Physio_Private_Session]: {category: 'Physiotherapist', subcategory: 'Physio Private Session', updateCategoryName: 'Physiotherapist Private Session Review'},
    },
    [Sections.Request_For_Items]: {
      [Request_For_Items_SubSections.Clothing_Footwear]: {category: 'Request for Items', subcategory: 'Clothing/Footwear', updateCategoryName: 'Items - Clothing / Footwear'},
      [Request_For_Items_SubSections.Food_Items]: {category: 'Request for Items', subcategory: 'Food Items', updateCategoryName: 'Items - Food items'},
      [Request_For_Items_SubSections.Personal_Care_Items]: {category: 'Request for Items', subcategory: 'Personal care items', updateCategoryName: 'Items - Personal care items'},
      [Request_For_Items_SubSections.Other]: {category: 'Request for Items', subcategory: 'Other', updateCategoryName: 'Items - Other'},
    },
    [Sections.Personal_Care]: {
      [Personal_Care_SubSections.Change_In_Habits]: {category: 'Personal Care Update', subcategory: 'Change in continence/bowel habits', updateCategoryName: 'Personal Care - Change in continence/bowel habits'},
      [Personal_Care_SubSections.Wearing_Of_Pads]: {category: 'Personal Care Update', subcategory: 'Wearing of Pads', updateCategoryName: 'Personal Care - Wearing of Pads'},
      [Personal_Care_SubSections.Change_In_Personal_Hygiene_Needs]: {category: 'Personal Care Update', subcategory: 'Change in personal hygiene needs (e.g. needs help with dressing etc)', updateCategoryName: 'Personal Care - Change in personal hygiene needs'},
    },
    [Sections.Nutritional]: {
      [Nutritional_SubSections.Nutritional]: {category: 'Nutrition and Dietary Update', subcategory: '', updateCategoryName: 'Nutrition and Dietary Update'},
    },
    [Sections.Specialist_Appointment]: {
      [Specialist_Appointment_SubSections.Referral_To_Allied_Health_Professiona]: {category: 'Appointment', subcategory: 'Allied Health Professional', updateCategoryName: 'Allied Health Professional Appointment'},
      [Specialist_Appointment_SubSections.Referral_To_Specialist]: {category: 'Appointment', subcategory: 'Specialist', updateCategoryName: 'Specialist Appointment'},
      [Specialist_Appointment_SubSections.General_Appointment]: {category: 'Appointment', subcategory: 'General', updateCategoryName: 'General Appointment'},
    },
    [Sections.Return_From_Hospital]: {
      [Return_From_Hospital_SubSections.Return_From_Hospital]: {category: 'Return from Hospital', subcategory: '', updateCategoryName: 'Return from Hospital'}
    },
    [Sections.Other]: {
      [Other_SubSections.Other]: {category: 'Other Update', subcategory: '', updateCategoryName: 'Other Update'}
    },
    [Sections.Resident_Of_The_Day]: {
      [Resident_Of_The_Day_SubSections.Resident_Of_The_Day]: {category: 'Resident of the Day ', subcategory: '', updateCategoryName: 'Resident of the Day '}
    },
    [Sections.Social]: {
      [Social_SubSections.Social]: {category: 'Social Update', subcategory: '', updateCategoryName: 'Social Update'}
    },
    [Sections.Monthly_Care_Statement]: {
      [Monthly_Care_Statement_SubSections.Monthly_Care_Statement]: {
        category: 'Monthly Care Statement',
        subcategory: '',
        updateCategoryName: 'Monthly Care Statement',
      },
    },
  }

  return {...sections[section][subSection], updateCategoryName: viewName || sections[section][subSection].updateCategoryName}
}

export function getUpdateCategoryNameById(sectionId:SectionsId, subSectionId: SubSectionsId) {
  const SubCategoriesNames = {
    [SectionsId.Fall]: {
      [Fall_SubSectionsId.General]: 'Fall',
      [Fall_SubSectionsId.Post_Fall_Review]: 'Post Fall Review',
    },
    [SectionsId.Infection]: {
      [Infection_SubSectionsId.Urinary_Infection]: 'Infection - Urinary Infection ',
      [Infection_SubSectionsId.Respiratory]: 'Infection - Respiratory',
      [Infection_SubSectionsId.Wound]: 'Infection - Wound',
      [Infection_SubSectionsId.Skin_Infection]: 'Infection - Skin Infection',
      [Infection_SubSectionsId.Cellulitis]: 'Infection - Cellulitis ',
      [Infection_SubSectionsId.Ear_Nose_Throat_ENT]: 'Infection - Ear, Nose, Throat ENT',
      [Infection_SubSectionsId.Covid_First_Update_To_Family]: 'Infection - Covid-19 First Update to Family',
      [Infection_SubSectionsId.Covid_Ongoing_Update]: 'Infection - Covid-19 Ongoing Update',
      [Infection_SubSectionsId.Eye]: 'Infection - Eye',
      [Infection_SubSectionsId.Other]: 'Infection - Unknown',
    },
    [SectionsId.Vaccination]: {
      [Vaccination_SubSectionsId.Vaccination]: 'Vaccination Administration'
    },
    [SectionsId.GP_Review_Medication_Change]: {
      [GP_Review_Medication_Change_SubSectionsId.General_Review]: 'GP Review / Medication Change - General Review',
      [GP_Review_Medication_Change_SubSectionsId.Deterioration_Physical_Or_Cognitive]: 'GP Review / Medication Change - Deterioration Physical or Cognitive ',
      [GP_Review_Medication_Change_SubSectionsId.Medication_Review]: 'GP Review / Medication Change - Medication Review',
      [GP_Review_Medication_Change_SubSectionsId.Review_Post_Fall_Or_Incident]: 'GP Review / Medication Change - Review Post Fall or Incident',
      [GP_Review_Medication_Change_SubSectionsId.Other]: 'GP Review / Medication Change - Other',
    },
    [SectionsId.Nurse_Practitioner]: {
      [Nurse_Practitioner_SubSectionsId.General_Review]: 'Nurse Practitioner - General Review',
      [Nurse_Practitioner_SubSectionsId.Deterioration_Physical_Or_Cognitive]: 'Nurse Practitioner - Deterioration Physical or Cognitive ',
      [Nurse_Practitioner_SubSectionsId.Medication_Review]: 'Nurse Practitioner - Medication Review',
      [Nurse_Practitioner_SubSectionsId.Review_Post_Fall_Or_Incident]: 'Nurse Practitioner - Review Post Fall or Incident',
      [Nurse_Practitioner_SubSectionsId.Other]: 'Nurse Practitioner - Other',
    },
    [SectionsId.Incident]: {
      [Incident_SubSectionsId.Bruising]: 'Incident - Bruising',
      [Incident_SubSectionsId.Skin_Tear]: 'Incident - Skin Tear',
      [Incident_SubSectionsId.Pressure_Injury]: 'Incident - Pressure Injury',
      [Incident_SubSectionsId.Ulcer]: 'Incident - Ulcer',
      [Incident_SubSectionsId.Absconding]: 'Incident - Absconding',
      [Incident_SubSectionsId.Behaviour]: 'Incident - Behaviour',
      [Incident_SubSectionsId.Choking]: 'Incident - Choking',
      [Incident_SubSectionsId.Follow_up]: 'Incident - Follow-up',
      [Incident_SubSectionsId.Incontinence_Associated_Dermatitis]: 'Incident - Incontinence Associated Dermatitis',
      [Incident_SubSectionsId.Other]: 'Incident - Other',
    },
    [SectionsId.Change_To_Condition]: {
      [Change_To_Condition_SubSectionsId.Change_To_Cognition]: 'Change to condition - Change to cognition',
      [Change_To_Condition_SubSectionsId.Change_To_Mobility]: 'Change to condition - Change to mobility',
      [Change_To_Condition_SubSectionsId.Change_To_Appetite]: 'Change to condition - Change to appetite / nutrition',
      [Change_To_Condition_SubSectionsId.Change_To_Behaviour_Mood]: 'Change to condition - change to behaviour / mood',
      [Change_To_Condition_SubSectionsId.Weight_Change_Update]: 'Change to condition - Weight change update',
      [Change_To_Condition_SubSectionsId.Other_General_Update]: 'Change to condition - other / general update',
    },
    [SectionsId.Wound_Care]: {
      [Wound_Care_SubSectionsId.Wound_Healed]: 'Wound Care - Wound Healing',
      [Wound_Care_SubSectionsId.Wound_Review]: 'Wound Care - Wound Review',
    },
    [SectionsId.Medication_Incidents]: {
      [Medication_Incidents_SubSectionsId.Medication_Incidents]: 'Medication Incidents'
    },
    [SectionsId.Pain]: {
      [Pain_SubSectionsId.Pain]: 'Pain'
    },
    [SectionsId.Physiotherapist]: {
      [Physiotherapist_SubSectionsId.Initial_Physio_Review]: 'Physiotherapist - Initial Physio Review',
      [Physiotherapist_SubSectionsId.Physio_Post_Fall_Review]: 'Physiotherapist - Physio Post Fall Review',
      [Physiotherapist_SubSectionsId.Physio_Mobility_Equipment_Review]: 'Physiotherapist - Physio Mobility/Equipment Review',
      [Physiotherapist_SubSectionsId.Physio_Private_Session]: 'Physiotherapist - Physio Private Session',
      [Physiotherapist_SubSectionsId.Group_Physio_Led_Exercise_Session]: 'Physiotherapist - Group Physio Led Exercise Session',
    },
    [SectionsId.Request_For_Items]: {
      [Request_For_Items_SubSectionsId.Clothing_Footwear]: 'Request for Items - Clothing / Footwear',
      [Request_For_Items_SubSectionsId.Food_Items]: 'Request for Items - Food items',
      [Request_For_Items_SubSectionsId.Personal_Care_Items]: 'Request for Items - Personal care items',
      [Request_For_Items_SubSectionsId.Other]: 'Request for Items - Other',
    },
    [SectionsId.Personal_Care]: {
      [Personal_Care_SubSectionsId.Change_In_Habits]: 'Personal Care Update: Change in continence/bowel habits',
      [Personal_Care_SubSectionsId.Wearing_Of_Pads]: 'Personal Care Update: Wearing of Pads',
      [Personal_Care_SubSectionsId.Change_In_Personal_Hygiene_Needs]: 'Personal Care Update: Change in personal hygiene needs (e.g. needs help with dressing etc)',
    },
    [SectionsId.Nutritional]: {
      [Nutritional_SubSectionsId.Nutritional]: 'Nutrition and Dietary Update',
    },
    [SectionsId.Specialist_Appointment]: {
      [Specialist_Appointment_SubSectionsId.Referral_To_Allied_Health_Professiona]: 'Appointment - Allied Health Professional Appointment',
      [Specialist_Appointment_SubSectionsId.Referral_To_Specialist]: 'Appointment - Specialist Appointment',
      [Specialist_Appointment_SubSectionsId.General_Appointment]: 'Appointment - General Appointment',
    },
    [SectionsId.Return_From_Hospital]: {
      [Return_From_Hospital_SubSectionsId.Return_From_Hospital]: 'Return from Hospital'
    },
    [SectionsId.Resident_Of_The_Day]: {
      [Resident_Of_The_Day_SubSectionsId.Resident_Of_The_Day]: 'Resident of the Day'
    },
    [SectionsId.Other]: {
      [Other_SubSectionsId.Other]: 'Other update'
    },
    [SectionsId.Social]: {
      [Social_SubSectionsId.Social]: 'Social update'
    },
    [SectionsId.Monthly_Care_Statement]: {
      [MonthlyCareStatement_SubSectionsId.Monthly_Care_Statement]: 'Monthly Care Statement',
    },
  }
  
  return SubCategoriesNames[sectionId][subSectionId]
}

export function getSubSectionNameById(sectionId:SectionsId, subSectionId: SubSectionsId): SubSections {
  const SubCategories = {
    [SectionsId.Fall]: {
      [Fall_SubSectionsId.General]: Fall_SubSections.General,
      [Fall_SubSectionsId.Post_Fall_Review]: Fall_SubSections.Post_Fall_Review,
    },
    [SectionsId.Infection]: {
      [Infection_SubSectionsId.Urinary_Infection]: Infection_SubSections.Urinary_Infection,
      [Infection_SubSectionsId.Respiratory]: Infection_SubSections.Respiratory,
      [Infection_SubSectionsId.Wound]: Infection_SubSections.Wound,
      [Infection_SubSectionsId.Skin_Infection]: Infection_SubSections.Skin_Infection,
      [Infection_SubSectionsId.Cellulitis]: Infection_SubSections.Cellulitis,
      [Infection_SubSectionsId.Ear_Nose_Throat_ENT]: Infection_SubSections.Ear_Nose_Throat_ENT,
      [Infection_SubSectionsId.Covid_First_Update_To_Family]: Infection_SubSections.Covid_First_Update_To_Family,
      [Infection_SubSectionsId.Covid_Ongoing_Update]: Infection_SubSections.Covid_Ongoing_Update,
      [Infection_SubSectionsId.Eye]: Infection_SubSections.Eye,
      [Infection_SubSectionsId.Other]: Infection_SubSections.Other,
    },
    [SectionsId.Vaccination]: {
      [Vaccination_SubSectionsId.Vaccination]: Vaccination_SubSections.Vaccination,
    },
    [SectionsId.GP_Review_Medication_Change]: {
      [GP_Review_Medication_Change_SubSectionsId.General_Review]: GP_Review_Medication_Change_SubSections.General_Review,
      [GP_Review_Medication_Change_SubSectionsId.Deterioration_Physical_Or_Cognitive]: GP_Review_Medication_Change_SubSections.Deterioration_Physical_Or_Cognitive,
      [GP_Review_Medication_Change_SubSectionsId.Medication_Review]: GP_Review_Medication_Change_SubSections.Medication_Review,
      [GP_Review_Medication_Change_SubSectionsId.Review_Post_Fall_Or_Incident]: GP_Review_Medication_Change_SubSections.Review_Post_Fall_Or_Incident,
      [GP_Review_Medication_Change_SubSectionsId.Other]: GP_Review_Medication_Change_SubSections.Other,
    },
    [SectionsId.Nurse_Practitioner]: {
      [Nurse_Practitioner_SubSectionsId.General_Review]: Nurse_Practitioner_SubSections.General_Review,
      [Nurse_Practitioner_SubSectionsId.Deterioration_Physical_Or_Cognitive]: Nurse_Practitioner_SubSections.Deterioration_Physical_Or_Cognitive,
      [Nurse_Practitioner_SubSectionsId.Medication_Review]: Nurse_Practitioner_SubSections.Medication_Review,
      [Nurse_Practitioner_SubSectionsId.Review_Post_Fall_Or_Incident]: Nurse_Practitioner_SubSections.Review_Post_Fall_Or_Incident,
      [Nurse_Practitioner_SubSectionsId.Other]: Nurse_Practitioner_SubSections.Other,
    },
    [SectionsId.Incident]: {
      [Incident_SubSectionsId.Bruising]: Incident_SubSections.Bruising,
      [Incident_SubSectionsId.Skin_Tear]: Incident_SubSections.Skin_Tear,
      [Incident_SubSectionsId.Pressure_Injury]: Incident_SubSections.Pressure_Injury,
      [Incident_SubSectionsId.Ulcer]: Incident_SubSections.Ulcer,
      [Incident_SubSectionsId.Absconding]: Incident_SubSections.Absconding,
      [Incident_SubSectionsId.Behaviour]: Incident_SubSections.Behaviour,
      [Incident_SubSectionsId.Choking]: Incident_SubSections.Choking,
      [Incident_SubSectionsId.Follow_up]: Incident_SubSections.Follow_up,
      [Incident_SubSectionsId.Incontinence_Associated_Dermatitis]: Incident_SubSections.Incontinence_Associated_Dermatitis,
      [Incident_SubSectionsId.Other]: Incident_SubSections.Other,
    },
    [SectionsId.Wound_Care]: {
      [Wound_Care_SubSectionsId.Wound_Healed]: Wound_Care_SubSections.Wound_Healed,
      [Wound_Care_SubSectionsId.Wound_Review]: Wound_Care_SubSections.Wound_Review,
    },
    [SectionsId.Change_To_Condition]: {
      [Change_To_Condition_SubSectionsId.Change_To_Cognition]: Change_To_Condition_SubSections.Change_To_Cognition,
      [Change_To_Condition_SubSectionsId.Change_To_Mobility]: Change_To_Condition_SubSections.Change_To_Mobility,
      [Change_To_Condition_SubSectionsId.Change_To_Appetite]: Change_To_Condition_SubSections.Change_To_Appetite,
      [Change_To_Condition_SubSectionsId.Change_To_Behaviour_Mood]: Change_To_Condition_SubSections.Change_To_Behaviour_Mood,
      [Change_To_Condition_SubSectionsId.Weight_Change_Update]: Change_To_Condition_SubSections.Weight_Change_Update,
      [Change_To_Condition_SubSectionsId.Other_General_Update]: Change_To_Condition_SubSections.Other_General_Update,
    },
    [SectionsId.Medication_Incidents]: {
      [Medication_Incidents_SubSectionsId.Medication_Incidents]: Medication_Incidents_SubSections.Medication_Incidents
    },
    [SectionsId.Pain]: {
      [Pain_SubSectionsId.Pain]: Pain_SubSections.Pain
    },
    [SectionsId.Physiotherapist]: {
      [Physiotherapist_SubSectionsId.Initial_Physio_Review]: Physiotherapist_SubSections.Initial_Physio_Review,
      [Physiotherapist_SubSectionsId.Physio_Post_Fall_Review]: Physiotherapist_SubSections.Physio_Post_Fall_Review,
      [Physiotherapist_SubSectionsId.Physio_Mobility_Equipment_Review]: Physiotherapist_SubSections.Physio_Mobility_Equipment_Review,
      [Physiotherapist_SubSectionsId.Physio_Private_Session]: Physiotherapist_SubSections.Physio_Private_Session,
      [Physiotherapist_SubSectionsId.Group_Physio_Led_Exercise_Session]: Physiotherapist_SubSections.Group_Physio_Led_Exercise_Session,
    },
    [SectionsId.Request_For_Items]: {
      [Request_For_Items_SubSectionsId.Clothing_Footwear]: Request_For_Items_SubSections.Clothing_Footwear,
      [Request_For_Items_SubSectionsId.Food_Items]: Request_For_Items_SubSections.Food_Items,
      [Request_For_Items_SubSectionsId.Personal_Care_Items]: Request_For_Items_SubSections.Personal_Care_Items,
      [Request_For_Items_SubSectionsId.Other]: Request_For_Items_SubSections.Other,
    },
    [SectionsId.Personal_Care]: {
      [Personal_Care_SubSectionsId.Change_In_Habits]: Personal_Care_SubSections.Change_In_Habits,
      [Personal_Care_SubSectionsId.Wearing_Of_Pads]: Personal_Care_SubSections.Wearing_Of_Pads,
      [Personal_Care_SubSectionsId.Change_In_Personal_Hygiene_Needs]: Personal_Care_SubSections.Change_In_Personal_Hygiene_Needs,
    },
    [SectionsId.Nutritional]: {
      [Nutritional_SubSectionsId.Nutritional]: Nutritional_SubSections.Nutritional,
    },
    [SectionsId.Specialist_Appointment]: {
      [Specialist_Appointment_SubSectionsId.Referral_To_Allied_Health_Professiona]: Specialist_Appointment_SubSections.Referral_To_Allied_Health_Professiona,
      [Specialist_Appointment_SubSectionsId.Referral_To_Specialist]: Specialist_Appointment_SubSections.Referral_To_Specialist,
      [Specialist_Appointment_SubSectionsId.General_Appointment]: Specialist_Appointment_SubSections.General_Appointment,
    },
    [SectionsId.Return_From_Hospital]: {
      [Return_From_Hospital_SubSectionsId.Return_From_Hospital]: Return_From_Hospital_SubSections.Return_From_Hospital
    },
    [SectionsId.Other]: {
      [Other_SubSectionsId.Other]: Other_SubSections.Other
    },
    [SectionsId.Resident_Of_The_Day]: {
      [Resident_Of_The_Day_SubSectionsId.Resident_Of_The_Day]: Resident_Of_The_Day_SubSections.Resident_Of_The_Day
    },
    [SectionsId.Social]: {
      [Social_SubSectionsId.Social]: Social_SubSections.Social
    },
    [SectionsId.Monthly_Care_Statement]: {
      [MonthlyCareStatement_SubSectionsId.Monthly_Care_Statement]: Monthly_Care_Statement_SubSections.Monthly_Care_Statement,
    },
  }
  
  return SubCategories[sectionId][subSectionId]
}

export function getSectionId(categoryName: Sections | ''): SectionsId | '' {
  const Categories = {
    [Sections.Fall]: SectionsId.Fall,
    [Sections.Infection]: SectionsId.Infection,
    [Sections.Vaccination]: SectionsId.Vaccination,
    [Sections.GP_Review_Medication_Change]: SectionsId.GP_Review_Medication_Change,
    [Sections.Nurse_Practitioner]: SectionsId.Nurse_Practitioner,
    [Sections.Incident]: SectionsId.Incident,
    [Sections.Wound_Care]: SectionsId.Wound_Care,
    [Sections.Change_To_Condition]: SectionsId.Change_To_Condition,
    [Sections.Medication_Incidents]: SectionsId.Medication_Incidents,
    [Sections.Pain]: SectionsId.Pain,
    [Sections.Physiotherapist]: SectionsId.Physiotherapist,
    [Sections.Request_For_Items]: SectionsId.Request_For_Items,
    [Sections.Personal_Care]: SectionsId.Personal_Care,
    [Sections.Nutritional]: SectionsId.Nutritional,
    [Sections.Specialist_Appointment]: SectionsId.Specialist_Appointment,
    [Sections.Other]: SectionsId.Other,
    [Sections.Resident_Of_The_Day]: SectionsId.Resident_Of_The_Day,
    [Sections.Return_From_Hospital]: SectionsId.Return_From_Hospital,
    [Sections.Social]: SectionsId.Social,
    [Sections.Monthly_Care_Statement]: SectionsId.Monthly_Care_Statement,
  }

  return categoryName ? Categories[categoryName] : ''
}

export function getSubSectionId(categoryName:Sections | '', subCategoryName: SubSections | ''): SubSectionsId | '' {
  const SubCategories = {
    [Sections.Fall]: {
      [Fall_SubSections.General]: Fall_SubSectionsId.General,
      [Fall_SubSections.Post_Fall_Review]: Fall_SubSectionsId.Post_Fall_Review,
    },
    [Sections.Infection]: {
      [Infection_SubSections.Urinary_Infection]: Infection_SubSectionsId.Urinary_Infection,
      [Infection_SubSections.Respiratory]: Infection_SubSectionsId.Respiratory,
      [Infection_SubSections.Wound]: Infection_SubSectionsId.Wound,
      [Infection_SubSections.Skin_Infection]: Infection_SubSectionsId.Skin_Infection,
      [Infection_SubSections.Cellulitis]: Infection_SubSectionsId.Cellulitis,
      [Infection_SubSections.Ear_Nose_Throat_ENT]: Infection_SubSectionsId.Ear_Nose_Throat_ENT,
      [Infection_SubSections.Covid_First_Update_To_Family]: Infection_SubSectionsId.Covid_First_Update_To_Family,
      [Infection_SubSections.Covid_Ongoing_Update]: Infection_SubSectionsId.Covid_Ongoing_Update,
      [Infection_SubSections.Eye]: Infection_SubSectionsId.Eye,
      [Infection_SubSections.Other]: Infection_SubSectionsId.Other,
    },
    [Sections.Vaccination]: {
      [Vaccination_SubSections.Vaccination]: Vaccination_SubSectionsId.Vaccination
    },
    [Sections.GP_Review_Medication_Change]: {
      [GP_Review_Medication_Change_SubSections.General_Review]: GP_Review_Medication_Change_SubSectionsId.General_Review,
      [GP_Review_Medication_Change_SubSections.Deterioration_Physical_Or_Cognitive]: GP_Review_Medication_Change_SubSectionsId.Deterioration_Physical_Or_Cognitive,
      [GP_Review_Medication_Change_SubSections.Medication_Review]: GP_Review_Medication_Change_SubSectionsId.Medication_Review,
      [GP_Review_Medication_Change_SubSections.Review_Post_Fall_Or_Incident]: GP_Review_Medication_Change_SubSectionsId.Review_Post_Fall_Or_Incident,
      [GP_Review_Medication_Change_SubSections.Other]: GP_Review_Medication_Change_SubSectionsId.Other,
    },
    [Sections.Nurse_Practitioner]: {
      [Nurse_Practitioner_SubSections.General_Review]: Nurse_Practitioner_SubSectionsId.General_Review,
      [Nurse_Practitioner_SubSections.Deterioration_Physical_Or_Cognitive]: Nurse_Practitioner_SubSectionsId.Deterioration_Physical_Or_Cognitive,
      [Nurse_Practitioner_SubSections.Medication_Review]: Nurse_Practitioner_SubSectionsId.Medication_Review,
      [Nurse_Practitioner_SubSections.Review_Post_Fall_Or_Incident]: Nurse_Practitioner_SubSectionsId.Review_Post_Fall_Or_Incident,
      [Nurse_Practitioner_SubSections.Other]: Nurse_Practitioner_SubSectionsId.Other,
    },
    [Sections.Incident]: {
      [Incident_SubSections.Bruising]: Incident_SubSectionsId.Bruising,
      [Incident_SubSections.Skin_Tear]: Incident_SubSectionsId.Skin_Tear,
      [Incident_SubSections.Pressure_Injury]: Incident_SubSectionsId.Pressure_Injury,
      [Incident_SubSections.Ulcer]: Incident_SubSectionsId.Ulcer,
      [Incident_SubSections.Absconding]: Incident_SubSectionsId.Absconding,
      [Incident_SubSections.Behaviour]: Incident_SubSectionsId.Behaviour,
      [Incident_SubSections.Choking]: Incident_SubSectionsId.Choking,
      [Incident_SubSections.Follow_up]: Incident_SubSectionsId.Follow_up,
      [Incident_SubSections.Incontinence_Associated_Dermatitis]: Incident_SubSectionsId.Incontinence_Associated_Dermatitis,
      [Incident_SubSections.Other]: Incident_SubSectionsId.Other,
    },
    [Sections.Wound_Care]: {
      [Wound_Care_SubSections.Wound_Healed]: Wound_Care_SubSectionsId.Wound_Healed,
      [Wound_Care_SubSections.Wound_Review]: Wound_Care_SubSectionsId.Wound_Review,
    },
    [Sections.Change_To_Condition]: {
      [Change_To_Condition_SubSections.Change_To_Cognition]: Change_To_Condition_SubSectionsId.Change_To_Cognition,
      [Change_To_Condition_SubSections.Change_To_Mobility]: Change_To_Condition_SubSectionsId.Change_To_Mobility,
      [Change_To_Condition_SubSections.Change_To_Appetite]: Change_To_Condition_SubSectionsId.Change_To_Appetite,
      [Change_To_Condition_SubSections.Change_To_Behaviour_Mood]: Change_To_Condition_SubSectionsId.Change_To_Behaviour_Mood,
      [Change_To_Condition_SubSections.Weight_Change_Update]: Change_To_Condition_SubSectionsId.Weight_Change_Update,
      [Change_To_Condition_SubSections.Other_General_Update]: Change_To_Condition_SubSectionsId.Other_General_Update,
    },
    [Sections.Medication_Incidents]: {
      [Medication_Incidents_SubSections.Medication_Incidents]: Medication_Incidents_SubSectionsId.Medication_Incidents
    },
    [Sections.Pain]: {
      [Pain_SubSections.Pain]: Pain_SubSectionsId.Pain
    },
    [Sections.Physiotherapist]: {
      [Physiotherapist_SubSections.Initial_Physio_Review]: Physiotherapist_SubSectionsId.Initial_Physio_Review,
      [Physiotherapist_SubSections.Physio_Post_Fall_Review]: Physiotherapist_SubSectionsId.Physio_Post_Fall_Review,
      [Physiotherapist_SubSections.Physio_Mobility_Equipment_Review]: Physiotherapist_SubSectionsId.Physio_Mobility_Equipment_Review,
      [Physiotherapist_SubSections.Physio_Private_Session]: Physiotherapist_SubSectionsId.Physio_Private_Session,
      [Physiotherapist_SubSections.Group_Physio_Led_Exercise_Session]: Physiotherapist_SubSectionsId.Group_Physio_Led_Exercise_Session,
    },

    [Sections.Request_For_Items]: {
      [Request_For_Items_SubSections.Clothing_Footwear]: Request_For_Items_SubSectionsId.Clothing_Footwear,
      [Request_For_Items_SubSections.Food_Items]: Request_For_Items_SubSectionsId.Food_Items,
      [Request_For_Items_SubSections.Personal_Care_Items]: Request_For_Items_SubSectionsId.Personal_Care_Items,
      [Request_For_Items_SubSections.Other]: Request_For_Items_SubSectionsId.Other,
    },
    [Sections.Personal_Care]: {
      [Personal_Care_SubSections.Change_In_Habits]: Personal_Care_SubSectionsId.Change_In_Habits,
      [Personal_Care_SubSections.Wearing_Of_Pads]: Personal_Care_SubSectionsId.Wearing_Of_Pads,
      [Personal_Care_SubSections.Change_In_Personal_Hygiene_Needs]: Personal_Care_SubSectionsId.Change_In_Personal_Hygiene_Needs,
    },
    [Sections.Nutritional]: {
      [Nutritional_SubSections.Nutritional]: Nutritional_SubSectionsId.Nutritional,
    },
    [Sections.Specialist_Appointment]: {
      [Specialist_Appointment_SubSections.Referral_To_Allied_Health_Professiona]: Specialist_Appointment_SubSectionsId.Referral_To_Allied_Health_Professiona,
      [Specialist_Appointment_SubSections.Referral_To_Specialist]: Specialist_Appointment_SubSectionsId.Referral_To_Specialist,
      [Specialist_Appointment_SubSections.General_Appointment]: Specialist_Appointment_SubSectionsId.General_Appointment,
    },
    [Sections.Return_From_Hospital]: {
      [Return_From_Hospital_SubSections.Return_From_Hospital]: Return_From_Hospital_SubSectionsId.Return_From_Hospital
    },
    [Sections.Other]: {
      [Other_SubSections.Other]: Other_SubSectionsId.Other
    },
    [Sections.Resident_Of_The_Day]: {
      [Resident_Of_The_Day_SubSections.Resident_Of_The_Day]: Resident_Of_The_Day_SubSectionsId.Resident_Of_The_Day
    },
    [Sections.Social]: {
      [Social_SubSections.Social]: Social_SubSectionsId.Social
    },
    [Sections.Monthly_Care_Statement]: {
      [Monthly_Care_Statement_SubSections.Monthly_Care_Statement]: MonthlyCareStatement_SubSectionsId.Monthly_Care_Statement,
    },
  }

  return (categoryName && subCategoryName) ? SubCategories[categoryName][subCategoryName] : ''
}

export function getPriorityId(priorityName: UpdatePriorities | ''): UpdatePrioritiesId | '' {
  const priority = {
    [UpdatePriorities.Low]: UpdatePrioritiesId.Minor,
    [UpdatePriorities.Medium]: UpdatePrioritiesId.Major,
    [UpdatePriorities.High]: UpdatePrioritiesId.Critical,
    [UpdatePriorities.Social]: UpdatePrioritiesId.Social,
  }

  return priorityName ? priority[priorityName] : ''
}

export function getPriorityNameById(priorityId: UpdatePrioritiesId): UpdatePriorities {
  const priority = {
    [UpdatePrioritiesId.Minor]: UpdatePriorities.Low,
    [UpdatePrioritiesId.Major]: UpdatePriorities.Medium,
    [UpdatePrioritiesId.Critical]: UpdatePriorities.High,
    [UpdatePrioritiesId.Social]: UpdatePriorities.Social
  }

  return priority[priorityId]
}

export function generateAdditionalInfo(mainValues: IMainUpdateForm, formSteps: Array<IUpdateStep>) {
  return {
    inputValues: getStepsValues(formSteps)
  }
}

export function generateMessageString(valuesMain: IMainUpdateForm, inputsValues: IUpdateValues, isMultiple: boolean = false) {
  return isMultiple ? UpdateSchema[valuesMain.update][valuesMain.subCategory].messageMultiple(valuesMain, inputsValues) : UpdateSchema[valuesMain.update][valuesMain.subCategory].message(valuesMain, inputsValues);
}

export function formatMessage(message?: string) {

  return message && message.split(UpdateMessageSymbols.Line_Break).map((paragraph) => {
    return (<p className={styles.paragraph}>{paragraph}</p>)
  })
}

export function formatMessageCopy(message: string) {
    return message.replaceAll(UpdateMessageSymbols.Line_Break, '\n')
}

export function getCategoriesString(section: Sections | '', subSection: SubSections | '') {
  return `${getSectionId(section)}/${getSubSectionId(section, subSection)}`
}

export function hydrateSchema(section:Sections, subSection:SubSections, update?) {
  if(!update.additionalInfo?.inputValues) {
    return UpdateSchema[section][subSection];
  } 

  return {
    ...UpdateSchema[section][subSection],
    steps: UpdateSchema[section][subSection].steps.map((step: IUpdateStep, i) => {
      return {
        ...step,
        inputs: step.inputs.map((input, j) => {
          return({
          ...formateInput(input, update),
          validationSchema: UpdateSchema[section][subSection].steps[i].inputs[j].validationSchema || undefined
        })})
      }
    })
  } 
}

export function formateInput(input: UpdateInput, update?) {
  const values = update.additionalInfo.inputValues;
  if(!values) {
    return input
  }

  const cloneInput = deepClone(input);

  const formateInputValue = (input: UpdateInput, update) => {
    switch(input.type) {
      case UpdateInputsTypes.Checkbox: {
        return {...input, checked: values[input.name]}
      }
      case UpdateInputsTypes.CheckboxInput: {
        if(input.dependentInputs) {
          for(let key in input.dependentInputs) {
            input.dependentInputs[key] = input.dependentInputs[key].map((input) => formateInputValue(input, update))
          }
        }

        return {...input, value: values[input.name]}
      }
      case UpdateInputsTypes.RadioInput: {
        if(input.dependentInputs) {
          for(let key in input.dependentInputs) {
            input.dependentInputs[key] = input.dependentInputs[key].map((input) => formateInputValue(input, update))
          }
        }

        return {...input, value: values[input.name]}
      }
      case UpdateInputsTypes.File: {
        return {...input, value: values[input.name]}
      }
      default: {
        return {...input, value: values[input.name]}
      }
    }
  }

  return formateInputValue(cloneInput, update);
}

export function getStepsValues(steps: Array<IUpdateStep>) {
  const getInputsValues = (inputs: UpdateInputs) => {
    let values: IUpdateValues = {};

    inputs.forEach((input) => {
      const {type, name, value} = input;

      switch(type) {
        case UpdateInputsTypes.Checkbox: {
          values[name] = !!input.checked;
          break;
        }
        case UpdateInputsTypes.CheckboxInput: {
          if(input.dependentInputs) {
            Object.values(input.dependentInputs).forEach((inputs) => {
              values = {...values, ...getInputsValues(inputs)}
            })
          }
          values[input.name] = value;
          break;
        }
        case UpdateInputsTypes.RadioInput: {
          if(input.dependentInputs) {
            Object.values(input.dependentInputs).forEach((inputs) => {
              values = {...values, ...getInputsValues(inputs)}
            })
          }
          values[input.name] = value;
          break;
        }
        case UpdateInputsTypes.File: {
          values[input.name] = value;
          break;
        }
        default: {
          if (typeof value === 'string') {
            values[name] = value.trim();
          } else {
            values[name] = value;
          }
        }
      }
    })

    return values
  }

  let values: IUpdateValues = {};
  steps.forEach((step) => {
    values = {...values, ...getInputsValues(step.inputs)}
  });

  return values;
}

export function completeStep(steps: Array<IUpdateStep>, stepFormikValues: IUpdateValues, stepNum: number) {
  return steps.map((formStep, i) => {
    if(i !== stepNum) {
      return formStep;
    } else {
      const completeInputs = (inputs: UpdateInputs) => {
        return inputs.map((input) => {
          switch(input.type) {
            case UpdateInputsTypes.CheckboxInput: {
              if(input.dependentInputs) {
                for(let key in input.dependentInputs) {
                  input.dependentInputs[key] = completeInputs(input.dependentInputs[key])
                }

                return ({ 
                  ...input, 
                  value: stepFormikValues[input.name],
                })
              }

              return {...input, value: stepFormikValues[input.name] }
            }
            case UpdateInputsTypes.RadioInput: {
              if(input.dependentInputs) {
                for(let key in input.dependentInputs) {
                  input.dependentInputs[key] = completeInputs(input.dependentInputs[key])
                }

                return ({ 
                  ...input, 
                  value: stepFormikValues[input.name],
                })
              }

              return {...input, value: stepFormikValues[input.name] }
            }
            case UpdateInputsTypes.Checkbox: {
              return ({ 
                ...input, 
                checked: stepFormikValues[input.name]
              })
            }
            default: {
              return ({ 
                ...input, 
                value: stepFormikValues[input.name]
              })
            }
          }
        })
      }

      return {...formStep, inputs: completeInputs(formStep.inputs)}
    }
  })
}

export function getTimePrefix(section: Sections | '', subSection: SubSections | '', preview?: boolean) {
  if(preview) {
    switch(section) {
      case Sections.Pain: return 'It started on: ';
      case Sections.Infection: return 'It started on: ';
      default: return 'This happened: '
    }
  } else {
    switch(section) {
      case Sections.Pain: return 'When it started: ';
      case Sections.Infection: return 'When it started: ';
      default: return 'When it happened: '
    }
  }
}

export function formatSummaryMessage(summary: string) {
  let formatedStr = summary.replace(/\.\s*\./g, '.')
                            .replace(/\n/g, '').replace(/ +/g, ' ')
                            .replaceAll(UpdateMessageSymbols.Line_Break, '\n')
                            .replaceAll(UpdateMessageSymbols.Paragraph_Break, '/n')
                            .replace(/\/n +/g, UpdateMessageSymbols.Line_Break).trim()
        
  const formatedStrArr = formatedStr.split(UpdateMessageSymbols.Line_Break);

  if(formatedStrArr.length > 1 && !formatedStrArr[formatedStrArr.length-1]) {
    formatedStrArr.pop();
    formatedStr = formatedStrArr.join(UpdateMessageSymbols.Line_Break)
  }

  return formatedStr;
}

export const isImageFile = (file: File | { name: string; type?: string }) => {
  const imageFileNameRegex =
    /\.(avif|bmp|gif|heic|heif|ico|jpe?g|png|svg|tiff?|webp)$/i;
  return file.type
    ? file.type.startsWith('image/')
    : imageFileNameRegex.test(file.name);
};